import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  BookOpen, CheckCircle, PlayCircle, FileText, Clock,
  ChevronRight, ChevronDown, Lock, ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentCourses = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user) {
      fetchCourses();
    }
  }, [user, authLoading, navigate, slug]);

  const fetchCourses = async () => {
    try {
      if (slug) {
        const res = await axios.get(`${API}/student/course/${slug}`, { withCredentials: true });
        setSelectedCourse(res.data);
        // Expand first module by default
        if (res.data.modules?.length > 0) {
          setExpandedModules({ [res.data.modules[0].id]: true });
        }
      } else {
        const res = await axios.get(`${API}/student/courses`, { withCredentials: true });
        setCourses(res.data);
      }
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const markComplete = async (lessonId) => {
    try {
      await axios.post(`${API}/student/progress/${lessonId}`, {}, { withCredentials: true });
      toast.success('Lesson marked as complete!');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <PlayCircle className="w-5 h-5" />;
      case 'reading': return <BookOpen className="w-5 h-5" />;
      case 'assignment': return <FileText className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  // Course List View
  if (!slug) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/student/dashboard" className="text-[#94A3B8] hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">My Courses</h1>
              <p className="text-[#94A3B8]">Continue your learning journey</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => {
              const totalLessons = course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0;
              const completedLessons = course.modules?.reduce((acc, m) => 
                acc + (m.lessons?.filter(l => l.completed).length || 0), 0) || 0;
              const progress = totalLessons > 0 ? Math.round(completedLessons / totalLessons * 100) : 0;

              return (
                <Link
                  key={course.id}
                  to={`/student/courses/${course.slug}`}
                  className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden hover:border-[#00FFE0]/50 transition-all group"
                >
                  <div className="h-32 bg-gradient-to-br from-[#00AEEF] to-[#00FFE0] flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-[#060F22]" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{course.title}</h3>
                    <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">{course.description}</p>
                    <div className="flex items-center gap-2 text-[#94A3B8] text-sm mb-4">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration}</span>
                      <span className="mx-2">•</span>
                      <span>{course.modules?.length || 0} modules</span>
                    </div>
                    <div className="w-full bg-[#060F22] rounded-full h-2 mb-2">
                      <div 
                        className="bg-gradient-to-r from-[#00AEEF] to-[#00FFE0] h-2 rounded-full"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-[#94A3B8]">{progress}% complete</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Course Detail View
  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/student/courses" className="text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white">{selectedCourse?.title}</h1>
            <p className="text-[#94A3B8]">{selectedCourse?.description}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Modules Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {selectedCourse?.modules?.map((module, idx) => (
              <div key={module.id} className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleModule(module.id)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-[#060F22]/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center text-[#00AEEF] font-bold text-sm">
                      {idx + 1}
                    </span>
                    <span className="text-white font-medium">{module.title}</span>
                  </div>
                  {expandedModules[module.id] ? (
                    <ChevronDown className="w-5 h-5 text-[#94A3B8]" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
                  )}
                </button>
                
                {expandedModules[module.id] && (
                  <div className="border-t border-[#00AEEF]/10">
                    {module.lessons?.map((lesson) => (
                      <button
                        key={lesson.id}
                        onClick={() => setSelectedLesson(lesson)}
                        className={`w-full p-3 pl-6 flex items-center gap-3 text-left hover:bg-[#060F22]/50 transition-colors ${
                          selectedLesson?.id === lesson.id ? 'bg-[#060F22]/50 border-l-2 border-[#00FFE0]' : ''
                        }`}
                      >
                        <span className={lesson.completed ? 'text-[#00FFE0]' : 'text-[#94A3B8]'}>
                          {lesson.completed ? <CheckCircle className="w-5 h-5" /> : getLessonIcon(lesson.type)}
                        </span>
                        <div className="flex-1">
                          <p className={`text-sm ${lesson.completed ? 'text-[#00FFE0]' : 'text-white'}`}>
                            {lesson.title}
                          </p>
                          <p className="text-xs text-[#94A3B8]">{lesson.duration}</p>
                        </div>
                      </button>
                    ))}
                    {module.quiz && (
                      <Link
                        to={`/student/quiz/${module.quiz.id}`}
                        className="w-full p-3 pl-6 flex items-center gap-3 text-left hover:bg-[#060F22]/50 transition-colors border-t border-[#00AEEF]/10"
                      >
                        <FileText className="w-5 h-5 text-[#FF6A00]" />
                        <span className="text-sm text-[#FF6A00]">{module.quiz.title}</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Lesson Content */}
          <div className="lg:col-span-2">
            {selectedLesson ? (
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className={`p-2 rounded-lg ${
                    selectedLesson.type === 'video' ? 'bg-[#00AEEF]/20 text-[#00AEEF]' :
                    selectedLesson.type === 'assignment' ? 'bg-[#FF6A00]/20 text-[#FF6A00]' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {getLessonIcon(selectedLesson.type)}
                  </span>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedLesson.title}</h2>
                    <p className="text-[#94A3B8] text-sm">{selectedLesson.duration} • {selectedLesson.type}</p>
                  </div>
                </div>

                <div className="prose prose-invert max-w-none mb-8">
                  <p className="text-[#94A3B8] leading-relaxed whitespace-pre-wrap">{selectedLesson.content}</p>
                </div>

                {selectedLesson.type === 'assignment' ? (
                  <Link
                    to={`/student/assignment/${selectedLesson.id}`}
                    className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all"
                  >
                    Submit Assignment
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <button
                    onClick={() => markComplete(selectedLesson.id)}
                    disabled={selectedLesson.completed}
                    className={`inline-flex items-center gap-2 font-bold rounded-xl px-6 py-3 transition-all ${
                      selectedLesson.completed 
                        ? 'bg-[#00FFE0]/20 text-[#00FFE0] cursor-default'
                        : 'bg-[#00AEEF] text-white hover:bg-[#00AEEF]/80'
                    }`}
                  >
                    {selectedLesson.completed ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Completed
                      </>
                    ) : (
                      <>
                        Mark as Complete
                        <CheckCircle className="w-5 h-5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-8 text-center">
                <BookOpen className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Select a Lesson</h2>
                <p className="text-[#94A3B8]">Choose a lesson from the sidebar to start learning</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentCourses;
